import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';

// Modular components
import BasicRecordForm from './components/record-editor/BasicRecordForm';
import type { RecordFormData } from './components/record-editor/types';
import { getInitialFormData } from './components/record-editor/types';
import { validateRecordForm } from './components/record-editor/validation';
import { buildRecordPayload } from './components/record-editor/payloadBuilder';

interface NewRecordEditorProps {
  zoneName: string;
  onCreate: (record: {
    name: string;
    type: string;
    value: string;
    ttl: number;
    priority?: number;
    weight?: number;
    port?: number;
  }) => Promise<void>;
  onCancel: () => void;
}

const NewRecordEditor: React.FC<NewRecordEditorProps> = ({ zoneName, onCreate, onCancel }) => {
  const [formData, setFormData] = useState<RecordFormData>(getInitialFormData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors = validateRecordForm(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const payload = buildRecordPayload(formData);
      await onCreate(payload);
      onCancel();
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <motion.div
      className="card"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      <div className="card-header">
        <div className="flex items-center justify-between">
          <h3 className="text-heading-2 text-slate-900 dark:text-white">Add DNS Record</h3>
          <div className="text-body-small text-slate-500 dark:text-slate-400">{zoneName}</div>
        </div>
      </div>
      
      <div className="card-body">
        <form onSubmit={handleSubmit} className="space-y-6">
          <BasicRecordForm
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            zoneName={zoneName}
          />

          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200 dark:border-slate-700">
            <motion.button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center justify-center px-4 py-2 text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 font-medium rounded-lg transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={isSubmitting}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </motion.button>
            <motion.button
              type="submit"
              className="inline-flex items-center justify-center px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium rounded-lg transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Create Record
                </>
              )}
            </motion.button>
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default NewRecordEditor;
